import { useState } from "react";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { GALLERY } from "../data/site";
import useReveal from "../hooks/useReveal";

export default function Gallery() {
  const ref = useReveal();
  const [active, setActive] = useState(null);

  const open = (i) => setActive(i);
  const close = () => setActive(null);
  const next = () => setActive((i) => (i === null ? null : (i + 1) % GALLERY.length));
  const prev = () => setActive((i) => (i === null ? null : (i - 1 + GALLERY.length) % GALLERY.length));

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-28 sm:py-36 lg:py-44 bg-[#0A0A0C] grain"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="overline text-[#991B1B] mb-6">— Gallery</div>
            <h2
              data-testid="gallery-title"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[0.95]"
            >
              Frames & <span className="italic text-[#A0A0A8]">moments.</span>
            </h2>
          </div>
          <p className="text-[#A0A0A8] max-w-md text-sm leading-relaxed">
            A small selection of headshots and portraits. Hover to bring each frame to life. Click any image for a full view.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4">
          {GALLERY.map((img, i) => {
            const cls =
              img.span === "wide"
                ? "lg:col-span-8 aspect-[16/10]"
                : img.span === "tall"
                ? "lg:col-span-4 lg:row-span-2 aspect-[3/4] lg:aspect-[3/5]"
                : "lg:col-span-4 aspect-[4/5]";
            return (
              <button
                key={img.src}
                data-testid={`gallery-item-${i}`}
                onClick={() => open(i)}
                className={`group relative overflow-hidden bg-[#18181C] ${cls}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="gallery-img absolute inset-0 w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 50%, rgba(10,10,12,0.75) 100%)",
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500 opacity-0 group-hover:opacity-100">
                  <div className="overline text-[#D4AF37] mb-1">Frame {String(i + 1).padStart(2, "0")}</div>
                  <div className="text-white font-serif-display italic text-xl">{img.caption}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 sm:p-12"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 sm:left-8 text-white/70 hover:text-white p-3 border border-white/10 hover:border-[#991B1B] transition-colors"
            aria-label="Previous"
          >
            <ArrowLeft size={20} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 sm:right-8 text-white/70 hover:text-white p-3 border border-white/10 hover:border-[#991B1B] transition-colors"
            aria-label="Next"
          >
            <ArrowRight size={20} />
          </button>
          <div
            className="max-w-5xl max-h-[85vh] w-full h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={GALLERY[active].src}
              alt={GALLERY[active].alt}
              className="max-h-[80vh] w-auto object-contain"
            />
            <div className="overline text-[#D4AF37] mt-6">
              {String(active + 1).padStart(2, "0")} / {String(GALLERY.length).padStart(2, "0")} — {GALLERY[active].caption}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
